/**
 * Leads admin dashboard - paginated list with filters and tier KPI cards.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  Inbox,
  RefreshCw,
  Search,
  Calendar,
  Mail,
  Building2,
  Linkedin,
  Snowflake,
  Sun,
  Flame,
  Zap,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import LeadTierBadge from '../components/LeadTierBadge';
import leadAdminService from '../services/leadAdminService';
import type {
  Lead,
  LeadListFilters,
  LeadListResponse,
  LeadPersona,
  LeadStatus,
  LeadTier,
} from '../types/lead';

type SortKey = 'score_desc' | 'created_desc' | 'tier';

const PERSONA_OPTIONS: Array<{ value: LeadPersona; label: string }> = [
  { value: 'investor', label: 'Investor' },
  { value: 'startup', label: 'Startup' },
  { value: 'business_buyer', label: 'Business Buyer' },
  { value: 'professional', label: 'Professional' },
  { value: 'unknown', label: 'Unknown' },
];

const TIER_OPTIONS: Array<{ value: LeadTier; label: string }> = [
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot' },
  { value: 'red_hot', label: 'Red Hot' },
];

const STATUS_OPTIONS: Array<{ value: LeadStatus; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'scored', label: 'Scored' },
  { value: 'booking_offered', label: 'Booking Offered' },
  { value: 'booked', label: 'Booked' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'dismissed', label: 'Dismissed' },
];

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'score_desc', label: 'Score (high → low)' },
  { value: 'created_desc', label: 'Newest first' },
  { value: 'tier', label: 'Tier (red hot first)' },
];

const TIER_RANK: Record<LeadTier, number> = {
  cold: 0,
  warm: 1,
  hot: 2,
  red_hot: 3,
};

const STATUS_BADGE_CLASS: Record<LeadStatus, string> = {
  new: 'bg-blue-50 text-blue-700 border border-blue-100',
  scored: 'bg-gray-50 text-gray-700 border border-gray-200',
  booking_offered: 'bg-purple-50 text-purple-700 border border-purple-100',
  booked: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  contacted: 'bg-amber-50 text-amber-800 border border-amber-100',
  converted: 'bg-green-50 text-green-700 border border-green-100',
  dismissed: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const PERSONA_BADGE_CLASS: Record<LeadPersona, string> = {
  investor: 'bg-indigo-50 text-indigo-700',
  startup: 'bg-teal-50 text-teal-700',
  business_buyer: 'bg-amber-50 text-amber-800',
  professional: 'bg-sky-50 text-sky-700',
  unknown: 'bg-gray-100 text-gray-600',
};

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelative(iso?: string | null): string {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.round((Date.now() - then) / 1000);
    if (Number.isNaN(diff)) return '';
    if (diff < 60) return 'just now';
    if (diff < 3600) {
      const m = Math.round(diff / 60);
      return `${m} minute${m === 1 ? '' : 's'} ago`;
    }
    if (diff < 86400) {
      const h = Math.round(diff / 3600);
      return `${h} hour${h === 1 ? '' : 's'} ago`;
    }
    if (diff < 86400 * 30) {
      const d = Math.round(diff / 86400);
      return `${d} day${d === 1 ? '' : 's'} ago`;
    }
    if (diff < 86400 * 365) {
      const mo = Math.round(diff / (86400 * 30));
      return `${mo} month${mo === 1 ? '' : 's'} ago`;
    }
    const y = Math.round(diff / (86400 * 365));
    return `${y} year${y === 1 ? '' : 's'} ago`;
  } catch {
    return '';
  }
}

function scoreColorClass(score: number): string {
  if (score >= 75) return 'text-green-600';
  if (score >= 50) return 'text-orange-600';
  if (score >= 30) return 'text-amber-600';
  return 'text-gray-500';
}

const TIER_KPI_META: Array<{
  key: LeadTier;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  cardClass: string;
  iconClass: string;
}> = [
  {
    key: 'red_hot',
    label: 'Red Hot',
    icon: Zap,
    cardClass: 'border-red-200 bg-red-50/40',
    iconClass: 'text-red-600',
  },
  {
    key: 'hot',
    label: 'Hot',
    icon: Flame,
    cardClass: 'border-orange-200 bg-orange-50/40',
    iconClass: 'text-orange-500',
  },
  {
    key: 'warm',
    label: 'Warm',
    icon: Sun,
    cardClass: 'border-amber-200 bg-amber-50/40',
    iconClass: 'text-amber-500',
  },
  {
    key: 'cold',
    label: 'Cold',
    icon: Snowflake,
    cardClass: 'border-gray-200 bg-gray-50/60',
    iconClass: 'text-gray-400',
  },
];

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [leadList, setLeadList] = useState<LeadListResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<LeadListFilters>({ page: 1, size: 50 });
  const [searchInput, setSearchInput] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('score_desc');

  const loadLeads = async (current: LeadListFilters) => {
    setIsLoading(true);
    const result = await leadAdminService.fetchLeads(current);
    if (result.error) {
      toast.error(result.error);
      setLeadList(null);
    } else {
      setLeadList(result.data || null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadLeads(filters);
  }, [filters]);

  const handleFilterChange = <K extends keyof LeadListFilters>(
    key: K,
    value: LeadListFilters[K] | '',
  ) => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [key]: value === '' ? undefined : value,
    }));
  };

  const handleRefresh = () => loadLeads(filters);

  // Client-side search + sort over the server-paginated batch
  const visibleLeads: Lead[] = useMemo(() => {
    if (!leadList) return [];
    let leads = [...leadList.leads];
    const q = searchInput.trim().toLowerCase();
    if (q) {
      leads = leads.filter((l) => {
        return (
          (l.contact_name && l.contact_name.toLowerCase().includes(q)) ||
          (l.contact_email && l.contact_email.toLowerCase().includes(q)) ||
          (l.company_name && l.company_name.toLowerCase().includes(q))
        );
      });
    }
    if (sortKey === 'score_desc') {
      leads.sort((a, b) => b.total_score - a.total_score);
    } else if (sortKey === 'created_desc') {
      leads.sort((a, b) => (b.created_at > a.created_at ? 1 : -1));
    } else if (sortKey === 'tier') {
      leads.sort(
        (a, b) =>
          TIER_RANK[b.tier] - TIER_RANK[a.tier] || b.total_score - a.total_score,
      );
    }
    return leads;
  }, [leadList, searchInput, sortKey]);

  const tierCounts: Record<LeadTier, number> = useMemo(() => {
    const counts: Record<LeadTier, number> = {
      cold: 0,
      warm: 0,
      hot: 0,
      red_hot: 0,
    };
    if (leadList) {
      for (const l of leadList.leads) counts[l.tier] += 1;
    }
    return counts;
  }, [leadList]);

  const totalPages = leadList?.pages ?? 0;
  const currentPage = filters.page ?? 1;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-apple-ink">Leads</h1>
            <p className="text-sm text-apple-subtext mt-1">
              Captured submissions from <code className="text-xs">/connect</code> — score, triage, and promote.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {leadList && (
              <span className="inline-flex items-center px-2.5 py-1 bg-apple-bg border border-apple-divider rounded-full text-xs font-medium text-apple-ink">
                {leadList.total} total
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-2 bg-white border border-apple-divider rounded-lg text-sm font-medium text-apple-ink hover:bg-gray-50 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </button>
          </div>
        </div>

        {/* Tier KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TIER_KPI_META.map((meta) => {
            const Icon = meta.icon;
            return (
              <div
                key={meta.key}
                className={`rounded-xl border ${meta.cardClass} p-4 flex items-center justify-between`}
              >
                <div>
                  <div className="text-xs uppercase tracking-wide text-apple-subtext">
                    {meta.label}
                  </div>
                  <div className="text-2xl font-semibold text-apple-ink mt-1">
                    {tierCounts[meta.key]}
                  </div>
                </div>
                <Icon className={`w-7 h-7 ${meta.iconClass}`} />
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="sticky top-16 z-10 bg-apple-bg/80 backdrop-blur border border-apple-divider rounded-xl p-4 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-apple-subtext" />
              </div>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, email, or company..."
                className="block w-full pl-9 pr-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
              />
            </div>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  Sort: {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <select
              value={filters.tier || ''}
              onChange={(e) =>
                handleFilterChange('tier', e.target.value as LeadTier | '')
              }
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              <option value="">All tiers</option>
              {TIER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.persona || ''}
              onChange={(e) =>
                handleFilterChange('persona', e.target.value as LeadPersona | '')
              }
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              <option value="">All personas</option>
              {PERSONA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.status || ''}
              onChange={(e) =>
                handleFilterChange('status', e.target.value as LeadStatus | '')
              }
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-apple-subtext pointer-events-none" />
              <input
                type="date"
                value={filters.date_from?.slice(0, 10) || ''}
                onChange={(e) =>
                  handleFilterChange(
                    'date_from',
                    e.target.value ? new Date(e.target.value).toISOString() : '',
                  )
                }
                className="block w-full pl-9 pr-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
              />
            </div>

            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-apple-subtext pointer-events-none" />
              <input
                type="date"
                value={filters.date_to?.slice(0, 10) || ''}
                onChange={(e) =>
                  handleFilterChange(
                    'date_to',
                    e.target.value ? new Date(e.target.value).toISOString() : '',
                  )
                }
                className="block w-full pl-9 pr-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
              />
            </div>
          </div>
        </div>

        {/* Body states */}
        {isLoading ? (
          <div className="bg-white border border-apple-divider rounded-xl p-10 flex items-center justify-center text-apple-subtext">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading leads...
          </div>
        ) : !leadList || leadList.leads.length === 0 ? (
          <div className="bg-white border border-apple-divider rounded-xl p-12 text-center">
            <Inbox className="w-10 h-10 mx-auto text-apple-blue mb-4" />
            <h2 className="text-lg font-semibold text-apple-ink mb-2">
              No leads yet
            </h2>
            <p className="text-sm text-apple-subtext">
              Share <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">/connect</code>{' '}
              to start collecting submissions.
            </p>
          </div>
        ) : visibleLeads.length === 0 ? (
          <div className="bg-white border border-apple-divider rounded-xl p-10 text-center text-apple-subtext text-sm">
            No leads match the current search.
          </div>
        ) : (
          <div className="space-y-2">
            {visibleLeads.map((lead) => (
              <button
                key={lead.id}
                onClick={() => navigate(`/leads/${lead.id}`)}
                className="w-full text-left bg-white border border-apple-divider rounded-xl p-4 hover:border-apple-blue hover:shadow-card-hover transition-all"
              >
                <div className="flex items-center gap-4">
                  {/* Score column */}
                  <div className="flex-shrink-0 w-16 text-center">
                    <div
                      className={`text-3xl font-semibold tabular-nums ${scoreColorClass(
                        lead.total_score,
                      )}`}
                    >
                      {lead.total_score}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-apple-subtext">
                      score
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <LeadTierBadge tier={lead.tier} size="sm" />
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          PERSONA_BADGE_CLASS[lead.persona]
                        }`}
                      >
                        {formatLabel(lead.persona)}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          STATUS_BADGE_CLASS[lead.status]
                        }`}
                      >
                        {formatLabel(lead.status)}
                      </span>
                      {lead.booking_offered && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-700">
                          Booking offered
                        </span>
                      )}
                      {lead.booking_completed_at && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-50 text-emerald-700">
                          Booked
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      <span className="font-medium text-apple-ink truncate">
                        {lead.contact_name ||
                          lead.contact_email ||
                          'Anonymous lead'}
                      </span>
                      {lead.contact_email && lead.contact_name && (
                        <span className="inline-flex items-center text-xs text-apple-subtext">
                          <Mail className="w-3 h-3 mr-1" />
                          {lead.contact_email}
                        </span>
                      )}
                      {lead.company_name && (
                        <span className="inline-flex items-center text-xs text-apple-subtext">
                          <Building2 className="w-3 h-3 mr-1" />
                          {lead.company_name}
                        </span>
                      )}
                      {lead.linkedin_url && (
                        <span className="inline-flex items-center text-xs text-apple-subtext">
                          <Linkedin className="w-3 h-3 mr-1" />
                          LinkedIn
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right meta */}
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xs text-apple-subtext">
                      {formatRelative(lead.created_at)}
                    </div>
                    <div className="text-xs text-apple-subtext mt-0.5">
                      #{lead.id}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {leadList && totalPages > 1 && (
          <div className="flex items-center justify-between bg-white border border-apple-divider rounded-xl px-4 py-3">
            <div className="text-xs text-apple-subtext">
              Page {currentPage} of {totalPages} • {leadList.total} total
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    page: Math.max(1, (prev.page || 1) - 1),
                  }))
                }
                disabled={currentPage <= 1 || isLoading}
                className="px-3 py-1.5 text-sm border border-apple-divider rounded-lg text-apple-ink hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    page: Math.min(totalPages, (prev.page || 1) + 1),
                  }))
                }
                disabled={currentPage >= totalPages || isLoading}
                className="px-3 py-1.5 text-sm border border-apple-divider rounded-lg text-apple-ink hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LeadsPage;
